import {
  IValidationError,
  type IErrorApiResponse,
  type ISuccessApiResponse,
} from '@wyz/types';
import {
  NO_REFETCH_OPTIONS,
  OverridableQuery,
  UseMutationOptions,
  UseQueryOptions,
  restrictApi,
  useMutation,
  useQuery,
} from '@wyz/utils';
import { useApi, useConfig } from '~/common/context';
import { PurchaseQueryKeys } from '~/purchase/api';
import {
  IContainerSearchQuery,
  IContainerSearchResult,
  ICustomerContainerOrderGetOneById,
  ISubmitContainerOrderVariable,
  IValidateEansPayload,
  PurchaseErrorCodeKeys,
} from '~/purchase/types';

type Options = {
  getSearchResults?: OverridableQuery<
    UseQueryOptions<
      ISuccessApiResponse<IContainerSearchResult>,
      IErrorApiResponse
    >
  > & {
    args?: IContainerSearchQuery;
  };

  getSearchResultsMutation?: UseMutationOptions<
    ISuccessApiResponse<IContainerSearchResult>,
    IErrorApiResponse<IValidationError, PurchaseErrorCodeKeys>,
    IContainerSearchQuery
  >;
  submitOrder?: UseMutationOptions<
    ISuccessApiResponse<ICustomerContainerOrderGetOneById>,
    IErrorApiResponse<IValidationError, PurchaseErrorCodeKeys>,
    ISubmitContainerOrderVariable
  >;
  validateEans?: UseMutationOptions<
    ISuccessApiResponse,
    IErrorApiResponse<
      IValidationError<PurchaseErrorCodeKeys>,
      PurchaseErrorCodeKeys
    >,
    IValidateEansPayload
  >;
};

export const useManageCustomerContainerOrders = <
  TRequestedKeys extends keyof Options,
  TRequestedOptions extends Options = Pick<Options, TRequestedKeys>,
>(
  options: TRequestedOptions & {
    context: {
      customerId: string;
    };
  },
) => {
  const { callCoreApi } = useApi();
  const { tenantCode } = useConfig();

  const getSearchResultsMutation = useMutation({
    mutationFn: (variable) => {
      return callCoreApi<ISuccessApiResponse<IContainerSearchResult>>(
        `/offers/api/tenants/${tenantCode}/customers/${options.context.customerId}/container-offers/search`,
        {
          method: 'POST',
          body: JSON.stringify(variable),
        },
      );
    },
    ...options.getSearchResultsMutation,
  });
  const getSearchResultsQuery = useQuery({
    queryKey: PurchaseQueryKeys.containerOrder.searchResults(
      options.context.customerId,
      options.getSearchResults?.args,
    ),
    queryFn: () => {
      return callCoreApi<ISuccessApiResponse<IContainerSearchResult>>(
        `/offers/api/tenants/${tenantCode}/customers/${options.context.customerId}/container-offers/search`,
        {
          method: 'POST',
          body: JSON.stringify(options.getSearchResults?.args),
        },
      );
    },
    ...NO_REFETCH_OPTIONS,
    ...options.getSearchResults,
    placeholderData: (prevData) => prevData,
    enabled: Boolean(
      options.getSearchResults?.enabled && options.getSearchResults?.args,
    ),
  });

  const submitOrder = useMutation({
    mutationFn: (variable) => {
      return callCoreApi<
        ISuccessApiResponse<ICustomerContainerOrderGetOneById>
      >(
        `/offers/api/tenants/${tenantCode}/customers/${options.context.customerId}/container-orders`,
        {
          method: 'POST',
          body: JSON.stringify(variable.body),
        },
      );
    },
    ...options.submitOrder,
  });

  const validateEans = useMutation({
    mutationFn: (variable) => {
      return callCoreApi<ISuccessApiResponse>(
        `/offers/api/tenants/${tenantCode}/customers/${options.context.customerId}/container-offers/validate-eans`,
        {
          method: 'POST',
          body: JSON.stringify(variable),
        },
      );
    },
    ...options.validateEans,
  });

  return restrictApi(options, {
    getSearchResults: getSearchResultsQuery,
    getSearchResultsMutation,
    submitOrder,
    validateEans,
  });
};
